import { createFileRoute, Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { TopHeader } from "@/components/TopHeader";
import { BottomNavigation } from "@/components/BottomNavigation";
import { PropertyCard } from "@/components/PropertyCard";
import { MOCK_PROPERTIES, PROPERTY_TYPES, PURPOSES } from "@/lib/mock-data";
import { useState } from "react";

export const Route = createFileRoute("/search")({
  head: () => ({
    meta: [
      { title: "Search properties · Landoo" },
      { name: "description", content: "Search verified properties by location, type and purpose." },
      { property: "og:title", content: "Search properties · Landoo" },
      {
        property: "og:description",
        content: "Search verified properties by location, type and purpose.",
      },
    ],
  }),
  component: SearchPage,
});

function SearchPage() {
  const [q, setQ] = useState("");
  const [purpose, setPurpose] = useState<string>("all");
  const [type, setType] = useState<string>("all");

  const results = MOCK_PROPERTIES.filter((p) => {
    if (purpose !== "all" && p.purpose !== purpose) return false;
    if (type !== "all" && p.type !== type) return false;
    if (q && !`${p.title} ${p.location}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="flex min-h-screen flex-col bg-cream/30 pb-20 md:pb-0">
      <TopHeader />
      <main className="mx-auto w-full max-w-6xl flex-1 p-4 md:p-6">
        <div className="rounded-3xl border border-border bg-card p-4 shadow-soft">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by city, area or keyword"
              className="w-full rounded-full border border-border bg-secondary/60 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-brand focus:bg-background"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {PURPOSES.map((p) => (
              <button
                key={p.value}
                onClick={() => setPurpose(p.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  purpose === p.value
                    ? "bg-brown text-primary-foreground"
                    : "bg-secondary text-brown-dark hover:bg-brand-soft"
                }`}
              >
                {p.label}
              </button>
            ))}
            <div className="mx-2 h-6 w-px bg-border" />
            {PROPERTY_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => setType(t.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  type === t.value
                    ? "bg-brand text-brown-dark"
                    : "bg-secondary/70 text-brown-dark hover:bg-brand-soft"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">{results.length} results</div>
          <Link
            to="/"
            className="text-sm font-semibold text-brown-dark underline underline-offset-4"
          >
            Back to map
          </Link>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((p) => (
            <PropertyCard key={p.id} property={p} />
          ))}
        </div>
      </main>
      <BottomNavigation />
    </div>
  );
}
